import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { mergeMap, Observable, of, Subject, Subscription, switchMap, zip } from 'rxjs';
import { pick } from 'lodash';
import { EmbedProject, PROJECT_URGENCY_OBJECT, ProjectUrgency } from '@data/models/project.model';
import { Period, Sla, ServiceLevel } from '@data/models/sla-period.model';
import { SlaPeriodService } from '@data/services/sla-period.service';
import { ProjectWizardSlaLoadTemplateComponent } from '../project-wizard-sla-load-template/project-wizard-sla-load-template.component';
import { ProjectWizardCacheService } from '@modules/projects/cache/project-wizard-cache.service';

export const PERIOD_24X7 = 'PRD-000012';

export enum PeriodType {
    'HO',
    '24X7'
}

export const generateServiceLevelId = (urgency: ProjectUrgency, periodType: PeriodType) => {
    let periodTypePrefix = '';
    let urgencyPrefix = '';

    switch (periodType) {
        case PeriodType.HO:
            periodTypePrefix = 'BH';
            break;
        case PeriodType['24X7']:
            periodTypePrefix = '24X7';
            break;
    }

    switch (urgency) {
        case ProjectUrgency.MUST:
            urgencyPrefix = 'CRITICAL';
            break;
        case ProjectUrgency.SMALL:
            urgencyPrefix = 'NON_CRITICAL';
            break;
        case ProjectUrgency.STANDARD:
            urgencyPrefix = 'SPECIFIC';
    }

    return `${periodTypePrefix}_${urgencyPrefix}`;
};

@Component({
    selector: 'syn-project-wizard-sla',
    templateUrl: 'project-wizard-sla.component.html',
    styleUrls: [
        'project-wizard-sla.component.scss'
    ]
})
export class ProjectWizardSlaComponent implements OnInit, OnDestroy {

    @Input() public slaForm: FormGroup;

    public formValues: Observable<EmbedProject<undefined>>;

    public periodResponsePicker$: Observable<Period[]>;
    public periodReplyPicker$: Observable<Period[]>;
    public periodReplyPickerContentLoader = new Subject<{ term: string; strict: boolean }>();
    public periodResponsePickerContentLoader = new Subject<{ term: string; strict: boolean }>();

    public periodReplyTime: Period;
    public periodResponseTime: Period;

    public periods: Period[];

    public allowedUrgency: { value: ProjectUrgency; translate: string }[] = Object.values(PROJECT_URGENCY_OBJECT);

    private subscriptionToCopyEvent: Subscription;
    private subscriptionToPeriodReply: Subscription;
    private subscriptionToPeriodResponse: Subscription;
    private subscriptionToMultiSla: Subscription;
    private subscriptionListPeriods: Subscription;

    private previousPeriodIs24x7 = false;

    constructor(
        private readonly matDialog: MatDialog,
        private slaPeriodService: SlaPeriodService,
        private projectCacheService: ProjectWizardCacheService
    ) { }

    public get isMultiSla() {
        return this.slaForm.controls['multiSla'].value;
    }

    public ngOnInit(): void {
        this.periodReplyPicker$ = this.periodReplyPickerContentLoader.pipe(
            switchMap((search) => {
                const safeTerm = search.term.replace(/"/g, '');
                let filter = search.strict ? `name:~"${safeTerm}" kind:reply` : `${safeTerm} kind:reply`.trim();
                // Exclude non applicable value "7/7 - 00h00 à 00h00 - JF compris" for this picker (Période HO Contractuelle)
                filter += ' -name:"7/7 - 00h00 à 00h00 - JF compris"';
                return this.slaPeriodService.listPeriods({ filter: filter, limit: 20 });
            }),
        );
        this.periodResponsePicker$ = this.periodResponsePickerContentLoader.pipe(
            switchMap((search) => {
                const safeTerm = search.term.replace(/"/g, '');
                let filter = search.strict ? `name:~"${safeTerm}" kind:response` : `${safeTerm} kind:response`.trim();
                if (this.isMultiSla) {
                    filter += ` -id:"${PERIOD_24X7}"`;
                }
                return this.slaPeriodService.listPeriods({ filter: filter, limit: 20 });
            }),
        );

        this.subscriptionListPeriods = this.slaPeriodService.listPeriods({}).subscribe(periods => {
            this.periods = periods;
        });

        this.subscriptionToPeriodReply = this.slaForm.controls['periodReplyTime'].valueChanges.subscribe((it) => {
            this.periodReplyTime = it;
        });

        this.subscriptionToPeriodResponse = this.slaForm.controls['periodResponseTime'].valueChanges.subscribe(async (it) => {
            this.periodResponseTime = it;

            if (this.isMultiSla) {
                this.loadMultiSla();
            }
        });

        this.subscriptionToMultiSla = this.slaForm.controls['multiSla'].valueChanges.subscribe((value) => {
            const period = this.slaForm.controls['periodResponseTime'].value;

            if (value) {
                if (!period) {
                    return;
                }

                // In case the previous selected period is the 24x7.
                // The user has to select the HO period again.
                const is24x7 = period.id === PERIOD_24X7;
                this.previousPeriodIs24x7 = is24x7;
                if (is24x7) {
                    this.slaForm.controls['periodResponseTime'].reset();
                    return;
                }

                this.loadMultiSla();
            } else {
                this.slaForm.controls['urgency'].setValidators([Validators.required]);
                this.slaForm.controls['urgency'].updateValueAndValidity();

                this.slaForm.controls['defaultServiceLevel'].reset();
                this.slaForm.controls['serviceLevels'].reset([]);
            }
        });

        this.subscriptionToCopyEvent = this.projectCacheService.copyFromProjectEvent.subscribe(() => {
            this.slaForm.markAsTouched();
            this.loadValuesInForm();
        });

        this.loadValuesInForm();
    }

    public loadMultiSla() {
        const period = this.slaForm.controls['periodResponseTime'].value;
        if (!period) {
            return;
        }

        const preConfigs: ServiceLevel[] = [
            {
                id: generateServiceLevelId(ProjectUrgency.MUST, PeriodType.HO),
                urgency: ProjectUrgency.MUST,
                period: period.id
            },
            {
                id: generateServiceLevelId(ProjectUrgency.MUST, PeriodType['24X7']),
                urgency: ProjectUrgency.MUST,
                period: PERIOD_24X7
            },
            {
                id: generateServiceLevelId(ProjectUrgency.SMALL, PeriodType.HO),
                urgency: ProjectUrgency.SMALL,
                period: period.id
            },
            {
                id: generateServiceLevelId(ProjectUrgency.SMALL, PeriodType['24X7']),
                urgency: ProjectUrgency.SMALL,
                period: PERIOD_24X7
            },
            {
                id: generateServiceLevelId(ProjectUrgency.STANDARD, PeriodType.HO),
                urgency: ProjectUrgency.STANDARD,
                period: period.id
            },
            {
                id: generateServiceLevelId(ProjectUrgency.STANDARD, PeriodType['24X7']),
                urgency: ProjectUrgency.STANDARD,
                period: PERIOD_24X7
            }
        ];

        this.slaForm.get('serviceLevels').setValue(preConfigs);

        if (!this.slaForm.controls['defaultServiceLevel'].value) {
            const urgency = this.slaForm.controls['urgency'].value;

            if (this.previousPeriodIs24x7) {
                this.slaForm.controls['defaultServiceLevel'].setValue(
                    generateServiceLevelId(urgency, PeriodType['24X7'])
                );
            } else {
                const preConfigId: string | undefined = urgency?.length > 0 ? preConfigs.find(config => config.urgency === urgency)?.id : undefined;

                this.slaForm.controls['defaultServiceLevel'].setValue(preConfigId ?? preConfigs[0].id);
            }
        }

        this.slaForm.controls['urgency'].removeValidators([Validators.required]);
        this.slaForm.controls['urgency'].updateValueAndValidity();
    }

    public getSlaFormControls() {
        if (this.slaForm.controls['sla'] instanceof FormArray) {
            return this.slaForm.controls['sla'].controls;
        }

        return [];
    }

    public openLoadTemplateDialog() {
        const ref = this.matDialog.open(ProjectWizardSlaLoadTemplateComponent, {
            data: {
                loadSlas: (slas: Sla[]) => {
                    this.slaForm.markAsTouched();
                    this.slaForm.controls['sla'].patchValue(slas);
                    ref.close();
                }
            },
            width: '700px',
            autoFocus: false,
        });
    }

    public ngOnDestroy(): void {
        this.subscriptionToCopyEvent?.unsubscribe();
        this.subscriptionToPeriodReply?.unsubscribe();
        this.subscriptionToPeriodResponse?.unsubscribe();
        this.subscriptionToMultiSla?.unsubscribe();
        this.subscriptionListPeriods?.unsubscribe();
    }

    private loadValuesInForm(): void {
        this.formValues = zip([
            this.projectCacheService.project,
            this.projectCacheService.periods,
        ]).pipe(
            mergeMap(([project, periods]) => {
                const formValues = {
                    ...pick(project, [
                        'urgency',
                        'sla',
                        'defaultServiceLevel',
                        'serviceLevels'
                    ]),
                    periodReplyTime: periods[project.periodReplyTime],
                    periodResponseTime: periods[project.periodResponseTime],
                };

                this.slaForm.patchValue(formValues);
                this.slaForm.controls['multiSla'].setValue(formValues.serviceLevels && formValues.serviceLevels.length > 0, {
                    emitEvent: false
                });

                this.projectCacheService.setTabAsLoaded(3);
                return of(project);
            })
        );
    }
}
